import { useState } from 'react';
import type { ApproverDecision } from '@zevik/schemas';
import { BatchRefusedError, type FindingEntry } from '../api/client';
import {
  BATCH_LABELS,
  BATCHABLE_DISPOSITIONS,
  type BatchDraft,
  type BatchProblem,
  batchProblems,
  buildBatchRequest,
  emptyBatchDraft,
  problemsFrom,
} from '../review/batch';
import { Refused } from './credential/CredentialShell';

/** Records a selection under one verdict; resolves when the record has it. */
export type RecordBatch = (selection: readonly FindingEntry[], draft: BatchDraft) => Promise<void>;

/**
 * One verdict for a selection of findings (docs/DECISIONS.md ZD-127 §3).
 *
 * What is offered is `BATCHABLE_DISPOSITIONS`, read off the schema: accept
 * (remediate) and reject. A deferral, an accepted risk, a rewrite and a
 * dissent are judgements about one finding, refused in a batch by the record,
 * and so not offered here — the row's own decision control is where each is
 * made.
 *
 * Every refusal a reviewer reads on this bar is the API's sentence. Before
 * sending, `batchProblems` runs the same schema `recordDecisions` judges the
 * set with; after sending, a refusal arrives as that schema's issues and is
 * relayed with the finding each is about named by title, on `GateControl`'s
 * precedent (ZD-125 §3). Nothing here composes a rule of its own.
 *
 * The reason is typed once and travels once, on the batch. No member carries
 * it (ZD-127 §2).
 */
export function BatchBar({
  selection,
  standing,
  onRecord,
  onSelect,
  onClear,
}: {
  selection: readonly FindingEntry[];
  standing: ReadonlyMap<string, ApproverDecision>;
  onRecord: RecordBatch | null;
  onSelect: (findingId: string) => void;
  onClear: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<BatchDraft>(emptyBatchDraft);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [refused, setRefused] = useState<BatchProblem[] | null>(null);

  const problems = batchProblems(selection, draft);
  const alreadyDecided = selection.filter((entry) => standing.has(entry.finding_key)).length;

  const send = async (): Promise<void> => {
    if (onRecord === null) return;
    setFailure(null);
    setRefused(null);
    setSending(true);
    try {
      // The last gate before the wire: refuses rather than repairs, in the
      // browser — `batchProblems` is what the bar shows and this throws.
      buildBatchRequest(selection, draft);
      await onRecord(selection, draft);
      setDraft(emptyBatchDraft());
      onClear();
    } catch (error) {
      if (error instanceof BatchRefusedError) {
        setRefused(problemsFrom(error.issues, selection));
      } else {
        setFailure(error instanceof Error ? error.message : String(error));
      }
    } finally {
      setSending(false);
    }
  };

  const named = (problem: BatchProblem): React.JSX.Element => (
    <p>
      {problem.finding === null ? null : (
        <>
          <button
            type="button"
            className="submit--link"
            onClick={() => {
              onSelect(problem.finding!.finding_id);
            }}
          >
            {problem.finding.title}
          </button>
          {': '}
        </>
      )}
      {problem.message}.
    </p>
  );

  return (
    <section className="batch" aria-label="a decision for the selected findings">
      <p className="batch__lead">
        <strong>{String(selection.length)} selected.</strong> One decision for all of them, with one
        reason, recorded as a group — the record says each was decided, and that they were decided
        together.
        {alreadyDecided === 0
          ? ''
          : ` ${String(alreadyDecided)} of these already carry a decision; recording supersedes it, and both are kept.`}
        <button type="button" className="submit--link" onClick={onClear}>
          Clear the selection
        </button>
      </p>

      <fieldset>
        <legend>Is this true of their system?</legend>
        {BATCHABLE_DISPOSITIONS.map((disposition) => (
          <label className="choice" key={disposition}>
            <input
              type="radio"
              name="batch-disposition"
              checked={draft.disposition === disposition}
              onChange={() => {
                setDraft((current) => ({ ...current, disposition }));
              }}
            />
            <span>{BATCH_LABELS[disposition]}</span>
          </label>
        ))}
      </fieldset>

      <div>
        <label className="field" htmlFor="batch-reason">
          {draft.disposition === 'reject'
            ? 'Why, for the group (required)'
            : 'Why, for the group (optional)'}
        </label>
        <textarea
          id="batch-reason"
          value={draft.reason}
          onChange={(event) => {
            setDraft((current) => ({ ...current, reason: event.target.value }));
          }}
        />
      </div>

      {problems.length === 0 ? null : (
        <div className="decide__problems" role="status">
          {problems.map((problem, i) => (
            <span key={i}>{named(problem)}</span>
          ))}
        </div>
      )}
      {refused === null ? null : (
        <div className="decide__problems" role="alert">
          <p>The group was not recorded.</p>
          {refused.map((problem, i) => (
            <span key={i}>{named(problem)}</span>
          ))}
        </div>
      )}
      {failure === null ? null : <Refused message={failure} />}

      <div className="run__actions">
        <button
          type="button"
          className="submit"
          disabled={sending || onRecord === null || problems.length > 0}
          onClick={() => {
            void send();
          }}
        >
          {sending
            ? 'Recording…'
            : `Record for ${String(selection.length)} finding${selection.length === 1 ? '' : 's'}`}
        </button>
      </div>
      {onRecord === null ? (
        <p className="notice">
          This workspace is reading a committed fixture (?fixture), so a group decision cannot be
          recorded. Open it on a deployment without that parameter to record one.
        </p>
      ) : null}
    </section>
  );
}
